import { selectInventoryMeshSet } from '../../../../ft3d/engine/mesh-presentation.js';
import { selectWeaponMeshSetInState } from '../../../../ft3d/engine/weapon-state-roles.js';
import { lightSourceMeshOptions } from '../../encounter-creation-engine/light-source-runtime.js';
import type { Zone } from './floor-move.js';

type Row = Record<string, unknown>;
const record = (v: unknown): Row | null => v && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;

export function inventoryWorldMeshSet(row: Row, definition: Row | null, catalog: Row, heldState: string | null = null): ReturnType<typeof selectInventoryMeshSet> {
  const source = { ...row, assets: row.assets ?? definition?.assets, catalog };
  const value = (key: string): unknown => typeof ftLayerValue === 'function' ? ftLayerValue(source, key)
    : record(row.item_layer_types)?.[key] ?? record(definition?.item_layer_types)?.[key];
  return selectWeaponMeshSetInState(source, { role: 'world', bodyProfile: 'any', quality: 'automatic', entity: source,
    capacityLb: value('carrying_capacity_pounds'), ...lightSourceMeshOptions(source, value('bright_light_dim_light')) }, heldState);
}

/** Physical membership uses the selected look, independently of measurable bounds. */
export function inventoryMeshDrawn(row: Row, definition: Row | null, catalog: Row): boolean {
  return inventoryWorldMeshSet(row, definition, catalog) !== null;
}

/** Structural proxies and tree trunks have dedicated occupancy readers. */
export const dedicatedPropSpace = (itemType: string): boolean => ['tree', 'door', 'window', 'wall'].includes(itemType);

/** Use the world's selector, including standees, explicit looks, fullness and damage states. */
export function selectedPropMeshSpace(row: Row, definition: Row | null, catalog: Row): { boundsM: [number, number, number]; authoredSizeTypeId?: string } | null {
  const selected = inventoryWorldMeshSet(row, definition, catalog);
  if (!selected) return null;
  // Parts have no assembly offsets. Guessing a union would invent occupied space.
  if (selected.meshes.length !== 1) throw new Error('prop-mesh-assembly-unmeasured');
  const bounds = record(selected.meshes[0])?.boundsM;
  if (!Array.isArray(bounds) || bounds.length !== 3 || !bounds.every(n => Number.isFinite(Number(n)) && Number(n) > 0)) {
    throw new Error('prop-mesh-bounds-not-held');
  }
  const authored = record(selected.meshes[0])?.authoredSizeTypeId;
  return { boundsM: bounds.map(Number) as [number, number, number], ...(typeof authored === 'string' ? { authoredSizeTypeId: authored } : {}) };
}

/** Unmeasured world looks use the standee's size-tier cylinder. Other selector failures stay visible. */
export function selectedPropMeshSpaceOrTier(row: Row, definition: Row | null, catalog: Row): ReturnType<typeof selectedPropMeshSpace> {
  try { return selectedPropMeshSpace(row, definition, catalog); }
  catch (error) {
    if (error instanceof Error && (error.message === 'prop-mesh-bounds-not-held' || error.message === 'prop-mesh-assembly-unmeasured')) return null;
    throw error;
  }
}

interface PropPlacement {
  itemType?: string | null;
  runtime?: unknown;
  standee?: unknown;
  authoredBoundsM?: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } };
  transform?: { scale?: number; rotationDegrees?: number; rotationQuarterTurns?: number };
  footprintReceipt?: { widthFt: number; depthFt: number };
  reviewMetadata?: { rulesSizeFt?: number; propMeshSetSize?: number; propBoundsMeasured?: boolean };
}

/** Measured placement bounds include per-axis scaling; token drawing bounds never define prop space. */
export function propPlacementVolume(placement: PropPlacement, at: { x: number; y: number; z: number }): (Zone & { r: number }) | null {
  if (dedicatedPropSpace(placement.itemType ?? '')) return null;
  const bounds = placement.authoredBoundsM;
  if (placement.runtime && !placement.standee && bounds && placement.reviewMetadata?.propBoundsMeasured === true
    && (placement.reviewMetadata?.propMeshSetSize ?? 1) <= 1) {
    const scale = placement.transform?.scale ?? 1;
    const [width, height, depth] = (['x', 'y', 'z'] as const).map(axis =>
      (bounds.max[axis] - bounds.min[axis]) * scale / 0.3048);
    if ([width, height, depth].every(n => Number.isFinite(n) && n! > 0)) {
      const facing = -((placement.transform?.rotationDegrees ?? (placement.transform?.rotationQuarterTurns ?? 0) * 90) * Math.PI / 180);
      return { kind: 'oblong', x: at.x, y: at.y, halfW: width! / 2, halfD: depth! / 2, facing,
        r: Math.hypot(width!, depth!) / 2, zMin: at.z, zMax: at.z + height! };
    }
  }
  const size = placement.reviewMetadata?.rulesSizeFt ?? Math.max(placement.footprintReceipt?.widthFt ?? 5, placement.footprintReceipt?.depthFt ?? 5);
  return { kind: 'circle', x: at.x, y: at.y, r: size / 2, zMin: at.z, zMax: at.z + size };
}
